/**
 * Rule-based extractors for phone/email/pet, ported verbatim from the backend's
 * lead_facts/extractors.py — keep regexes in sync. Income/household/voucher use the LLM.
 */

/* Standard US formats only (optional country code, 3-3-4, one separator); strict on purpose. */
const PHONE_RE = /(?<!\d)(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/;

export function extractPhone(message: string): string | null {
    const match = PHONE_RE.exec(message);
    if (!match) {
        return null;
    }
    const digits = match[0].replace(/\D/g, '');
    if (digits.length === 10) {
        return `+1${digits}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
    }
    return null;
}

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

const EMAIL_TYPOS: Record<string, string> = Object.fromEntries(
    ['gmail', 'yahoo', 'hotmail', 'outlook', 'icloud', 'aol', 'msn', 'live'].map(
        (domain) => [`@${domain}.con`, `@${domain}.com`]
    )
);

export function extractEmail(message: string): string | null {
    const match = EMAIL_RE.exec(message);
    if (!match) {
        return null;
    }
    let email = match[0];
    for (const [typo, correction] of Object.entries(EMAIL_TYPOS)) {
        if (email.endsWith(typo)) {
            email = email.slice(0, -typo.length) + correction;
        }
    }
    return email;
}

const PET_TYPES = [
    'dogs?', 'cats?', 'birds?', 'fish', 'rabbits?', 'hamsters?',
    'turtles?', 'snakes?', 'lizards?', 'guinea pigs?', 'parrots?',
];
const PET_TYPE_RE = new RegExp(`\\b(${PET_TYPES.join('|')})\\b`, 'i');
const PET_POS_RE = /\b(pets?|animals?|mascotas?|esa\s+(dog|cat|animal)|emotional\s+support\s+(dog|cat|animal)|service\s+(dog|cat|animal))\b/i;
const PET_NEG_RE = /\b(no|don'?t|do\s+not|without|sin)\b.{0,20}\b(pets?|animals?|dogs?|cats?|mascotas?)\b|\b(pets?|animals?|dogs?|cats?|mascotas?)\b.{0,20}\b(no|not|none)\b/i;

/*
 * Phrases about the PROPERTY's pet policy, not the lead's own pet. Stripped before
 * deciding so they neither trip the negation nor look like possession.
 */
const PET_POLICY_SRC =
    'pets?[\\s\\W]{0,3}(?:friendly|allowed|welcome|ok(?:ay)?|polic|deposit|fee|rent|restrict)' +
    '|(?:no|any)[\\s\\W]{0,3}pets?[\\s\\W]{0,5}polic' +
    '|(?:allow|accept|take|permit)[\\s\\W]{0,3}(?:any\\s+)?pets?' +
    '|are\\s+pets?\\s+allow';

export function extractHasPet(message: string): boolean | null {
    const msg = message.replace(new RegExp(PET_POLICY_SRC, 'gi'), ' ');
    if (PET_NEG_RE.test(msg)) {
        return false;
    }
    if (PET_TYPE_RE.test(msg) || PET_POS_RE.test(msg)) {
        return true;
    }
    return null;
}

export function extractPetType(message: string): string | null {
    const match = PET_TYPE_RE.exec(message);
    if (!match) {
        return null;
    }
    const value = match[1].toLowerCase();
    return value.endsWith('s') && value !== 'fish' ? value.replace(/s+$/, '') : value;
}
